// ./src/utils/api/LookupDictionaryItemService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { LookupDictionary } from "./LookupDictionaryService";

// GraphQL queries
const LOOKUP_DICTIONARY_ITEM_LIST_QUERY = gql`
  query LookupDictionaryItemList(
    $filter: LookupDictionaryItemFilter
    $params: PaginationAndSorting
  ) {
    lookupDictionaryItemList(filter: $filter, params: $params) {
      code
      data {
        id
        lookupDictionaryId
        checkValue
        filter1
        filter2
        expiresAt
        description
        status
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const LOOKUP_DICTIONARY_ITEM_GET_QUERY = gql`
  query LookupDictionaryItemGet($id: ID) {
    lookupDictionaryItemGet(id: $id) {
      code
      data {
        id
        lookupDictionaryId
        checkValue
        filter1
        filter2
        expiresAt
        description
        status
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const LOOKUP_DICTIONARY_ITEM_GET_MANY_QUERY = gql`
  query LookupDictionaryItemGetMany($ids: [ID]) {
    lookupDictionaryItemGetMany(ids: $ids) {
      code
      data {
        id
        lookupDictionaryId
        checkValue
        filter1
        filter2
        expiresAt
        description
        status
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const LOOKUP_DICTIONARY_ITEM_CREATE_MUTATION = gql`
  mutation LookupDictionaryItemCreate($params: LookupDictionaryItemInput) {
    lookupDictionaryItemCreate(params: $params) {
      code
      data {
        id
        lookupDictionaryId
        checkValue
        filter1
        filter2
        expiresAt
        description
        status
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const LOOKUP_DICTIONARY_ITEM_UPDATE_MUTATION = gql`
  mutation LookupDictionaryItemUpdate(
    $where: LookupDictionaryItemWhereInput
    $params: LookupDictionaryItemInput
  ) {
    lookupDictionaryItemUpdate(where: $where, params: $params) {
      code
      data {
        id
        lookupDictionaryId
        checkValue
        filter1
        filter2
        expiresAt
        description
        status
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const LOOKUP_DICTIONARY_ITEM_REMOVE_MUTATION = gql`
  mutation LookupDictionaryItemRemove($where: LookupDictionaryItemWhereInput) {
    lookupDictionaryItemRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const LOOKUP_DICTIONARY_ITEM_REMOVE_MANY_MUTATION = gql`
  mutation LookupDictionaryItemRemoveMany(
    $where: [LookupDictionaryItemWhereInput]
  ) {
    lookupDictionaryItemRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface LookupDictionaryItem {
  id?: string | undefined;
  lookupDictionaryId?: string | undefined;
  checkValue?: string | undefined;
  filter1?: string | undefined;
  filter2?: string | undefined;
  expiresAt?: string | undefined;
  description?: string | undefined;
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  removedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
  userRemoved?: User;
  lookupDictionary?: LookupDictionary;
}

export interface LookupDictionaryItemInput {
  id?: string | null | undefined;
  lookupDictionaryId?: string | null | undefined;
  checkValue?: string | null | undefined;
  filter1?: string | null | undefined;
  filter2?: string | null | undefined;
  expiresAt?: string | null | undefined;
  description?: string | null | undefined;
  status?: number | null | undefined;
}

export interface LookupDictionaryItemFilter {
  id?: string[] | null | undefined;
  lookupDictionaryId?: string[] | null | undefined;
  checkValue?: string[] | null | undefined;
  filter1?: string[] | null | undefined;
  filter2?: string[] | null | undefined;
  isExpired?: boolean | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface LookupDictionaryItemWhereInput {
  id?: string | undefined;
}

/**
 * LookupDictionaryItem service that handles all lookup dictionary item-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class LookupDictionaryItemService {
  /**
   * Get a list of lookup dictionary items with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: LookupDictionaryItemFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      LOOKUP_DICTIONARY_ITEM_LIST_QUERY,
      {
        filter: filter || {},
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Get a single lookup dictionary item by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      LOOKUP_DICTIONARY_ITEM_GET_QUERY,
      {
        id: id,
      }
    );
    return result;
  }

  /**
   * Get multiple lookup dictionary items by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      LOOKUP_DICTIONARY_ITEM_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new lookup dictionary item
   */
  static async create(params: LookupDictionaryItemInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      LOOKUP_DICTIONARY_ITEM_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing lookup dictionary item
   */
  static async update(
    where: LookupDictionaryItemWhereInput,
    params: LookupDictionaryItemInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      LOOKUP_DICTIONARY_ITEM_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a lookup dictionary item
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      LOOKUP_DICTIONARY_ITEM_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple lookup dictionary items
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      LOOKUP_DICTIONARY_ITEM_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
